import React from "react";
import DSBox from "../../system-design/DSBox";
import DSAlert from "../../system-design/DSAlert";
import DSTextField from "../../system-design/DSTextField";
import DSDialog from "../../system-design/DSDialog";
import DSDialogTitle from "../../system-design/DSDialogTitle";
import DSDialogContent from "../../system-design/DSDialogContent";
import DSDialogActions from "../../system-design/DSDialogActions";
import DSPrimaryButton from "../../system-design/DSPrimaryButton";
import DSSecondaryButton from "../../system-design/DSSecondaryButton";
import DSCircularProgress from "../../system-design/DSCircularProgress";
import DSCheckBox from "../../system-design/DSCheckBox";
import DSFormControlLabel from "../../system-design/DSFormControlLabel";
import DSTypography from "../../system-design/DSTypography";

interface CreateCollectionCodeModalProps {
  show: boolean;
  orderName: string;
  selectedCollectionId: string;
  multiUse: boolean;
  expiresAt: string;
  showError: boolean;
  createdCode: string | null;
  isCreating: boolean;
  onClose: () => void;
  onOrderNameChange: (value: string) => void;
  onSelectCollection: () => void;
  onMultiUseChange: (value: boolean) => void;
  onExpiresAtChange: (value: string) => void;
  onCreateCode: () => void;
}

const todayInputValue = (): string => new Date().toISOString().split("T")[0];

function CreateCollectionCodeModal({
  show,
  orderName,
  selectedCollectionId,
  multiUse,
  expiresAt,
  showError,
  createdCode,
  isCreating,
  onClose,
  onOrderNameChange,
  onSelectCollection,
  onMultiUseChange,
  onExpiresAtChange,
  onCreateCode,
}: CreateCollectionCodeModalProps) {
  return (
    <DSDialog open={show} onClose={onClose} fullWidth maxWidth="sm">
      <DSDialogTitle>Create Collection Code</DSDialogTitle>
      <DSDialogContent>
        {showError && (
          <DSAlert severity="error" sx={{ mb: 2 }}>
            Both Order Name and Collection are required!
          </DSAlert>
        )}
        {createdCode && (
          <DSAlert severity="success" sx={{ mb: 2 }}>
            Code created successfully: <strong>{createdCode}</strong>
          </DSAlert>
        )}
        <DSBox sx={{ display: "flex", flexDirection: "column", gap: 3, mt: 1 }}>
          <DSTextField
            fullWidth
            label="Order Name"
            placeholder="Enter Order Name"
            value={orderName}
            onChange={(e) => onOrderNameChange(e.target.value)}
          />
          <DSBox>
            <DSSecondaryButton onClick={onSelectCollection}>
              {selectedCollectionId
                ? `Selected: ${selectedCollectionId}`
                : "Select Collection"}
            </DSSecondaryButton>
          </DSBox>
          <DSBox>
            <DSFormControlLabel
              control={
                <DSCheckBox
                  checked={multiUse}
                  onChange={(e) => onMultiUseChange(e.target.checked)}
                />
              }
              label="Multi-use code"
            />
            <DSTypography
              variant="caption"
              sx={{
                display: "block",
                ml: 4,
                color: "text.secondary",
              }}
            >
              Redeemable by any number of clocks. Leave off for a regular
              single-clock code.
            </DSTypography>
          </DSBox>
          <DSBox>
            <DSTypography
              variant="caption"
              sx={{ fontWeight: "bold", display: "block", mb: 0.5 }}
            >
              Expiration date (optional)
            </DSTypography>
            <DSBox sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <input
                type="date"
                value={expiresAt}
                min={todayInputValue()}
                onChange={(e) => onExpiresAtChange(e.target.value)}
                style={{
                  padding: "8px 12px",
                  borderRadius: "4px",
                  border: "1px solid #ccc",
                  fontSize: "14px",
                }}
              />
              {expiresAt && (
                <DSSecondaryButton onClick={() => onExpiresAtChange("")}>
                  Clear
                </DSSecondaryButton>
              )}
            </DSBox>
            <DSTypography
              variant="caption"
              sx={{ display: "block", mt: 0.5, color: "text.secondary" }}
            >
              Leave empty for a code that never expires.
            </DSTypography>
          </DSBox>
        </DSBox>
      </DSDialogContent>
      <DSDialogActions>
        <DSSecondaryButton onClick={onClose}>Close</DSSecondaryButton>
        <DSPrimaryButton
          onClick={onCreateCode}
          disabled={isCreating || !!createdCode}
        >
          {isCreating ? <DSCircularProgress size={20} /> : "Create Code"}
        </DSPrimaryButton>
      </DSDialogActions>
    </DSDialog>
  );
}

export default CreateCollectionCodeModal;
